import { keepPreviousData, queryOptions, useQuery } from "@tanstack/react-query"
import { GET, type Schema } from "@/api/client"
import type {
	CompaniesParams,
	CustomerCountParams,
	CustomersParams,
	LeadsParams,
} from "@/api/types"
import { SHORT_STALE_TIME } from "@/lib/constants"

export function useAccountOwners(status: "customer" | "lead") {
	return useQuery({
		queryKey: ["companies", "account-owners", status],
		queryFn: () =>
			GET("/api/v1/companies/account-owners", {
				params: { query: { status } },
			}),
		select: (owners: Schema["AccountOwnerOption"][] | undefined) =>
			(owners ?? []).map((o) => ({ value: o.email, label: o.name })),
	})
}

export function companiesOptions(params: CompaniesParams = {}) {
	return queryOptions({
		queryKey: ["companies", params],
		queryFn: () =>
			GET("/api/v1/companies", {
				params: { query: params },
			}),
		placeholderData: keepPreviousData,
	})
}

export function useCompanies(
	params: CompaniesParams = {},
	{ enabled = true }: { enabled?: boolean } = {},
) {
	return useQuery({ ...companiesOptions(params), enabled })
}

export function customersOptions(params: CustomersParams = {}) {
	return queryOptions({
		queryKey: ["companies", "customers", params],
		queryFn: () =>
			GET("/api/v1/companies/customers", {
				params: { query: params },
			}),
		placeholderData: keepPreviousData,
	})
}

export function useCustomers(
	params: CustomersParams = {},
	{ enabled = true }: { enabled?: boolean } = {},
) {
	return useQuery({ ...customersOptions(params), enabled })
}

export function customerCountOptions(filters: CustomerCountParams = {}) {
	return queryOptions({
		queryKey: ["companies", "customers", "count", filters],
		queryFn: () =>
			GET("/api/v1/companies/customers/count", {
				params: { query: filters },
			}),
	})
}

export function useCustomerCount(filters: CustomerCountParams = {}) {
	return useQuery(customerCountOptions(filters))
}

export function leadsOptions(params: LeadsParams = {}) {
	return queryOptions({
		queryKey: ["companies", "leads", params],
		queryFn: () =>
			GET("/api/v1/companies/leads", {
				params: { query: params },
			}),
		placeholderData: keepPreviousData,
	})
}

export function useLeads(params: LeadsParams = {}) {
	return useQuery(leadsOptions(params))
}

export function useLeadStats() {
	return useQuery({
		queryKey: ["companies", "leads", "stats"],
		queryFn: () => GET("/api/v1/companies/leads/stats"),
		staleTime: SHORT_STALE_TIME,
	})
}
